import { isDef } from '@utils/def';
import React, {
  Ref,
  forwardRef,
  useCallback,
  useImperativeHandle,
  useState,
} from 'react';

import { ICONS_TYPES } from '../Icon';

import { ButtonWrapper, PropertiesIcon, Root, StyledPopover } from './styles';
import { PopoverRef, Props } from './types';

const Popover = (
  {
    children,
    onClick,
    showByHover = false,
    renderChildren,
    className,
    anchorOrigin = {
      vertical: 'bottom',
      horizontal: 'left',
    },
    transformOrigin = {
      vertical: 'top',
      horizontal: 'left',
    },
  }: Props,
  ref: Ref<unknown> | undefined,
) => {
  const [anchorEl, setAnchorEl] = useState<HTMLDivElement | null>(null);
  const [open, setOpen] = useState(false);

  const handleClick = useCallback(
    (event: React.MouseEvent<HTMLDivElement>) => {
      event.stopPropagation();
      event.preventDefault();
      setAnchorEl(event.currentTarget);
      setOpen(true);
      onClick?.();
    },
    [onClick],
  );
  const handleMouseOver = useCallback(
    (event: React.MouseEvent<HTMLDivElement>) => {
      event.stopPropagation();
      event.preventDefault();
      setAnchorEl(event.currentTarget);
      setOpen(true);
    },
    [],
  );
  const handleClose = useCallback(() => {
    setOpen(false);
    setAnchorEl(null);
  }, []);

  useImperativeHandle(ref, () => ({
    close: () => {
      setOpen(false);
      setAnchorEl(null);
    },
    open: (anchor?: HTMLElement | null) => {
      if (anchor) setAnchorEl(anchor as HTMLDivElement);
      setOpen(true);
    },
  }));

  return (
    <Root className={className}>
      <ButtonWrapper
        tabIndex={-1}
        onClick={handleClick}
        onMouseEnter={showByHover ? handleMouseOver : undefined}>
        {isDef(children) ? (
          children
        ) : (
          <PropertiesIcon icon={ICONS_TYPES.Dots} size={18} open={open} />
        )}
      </ButtonWrapper>
      <StyledPopover
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        onClick={(event) => {
          event.stopPropagation();
        }}
        anchorOrigin={anchorOrigin}
        transformOrigin={transformOrigin}>
        {renderChildren()}
      </StyledPopover>
    </Root>
  );
};

export type { Props, PopoverRef };

export default forwardRef(Popover);
